import { NavigateNext as NavigateNextIcon } from "@mui/icons-material";
import {
  Breadcrumbs as MuiBreadcrumbs,
  BreadcrumbsProps as MuiBreadcrumbsProps,
  Link,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";

export interface BreadcrumbItem {
  label: string;
  href?: string;
  to?: string; // React Router path
  icon?: React.ReactNode;
  onClick?: () => void;
}

export interface BreadcrumbsProps extends Omit<MuiBreadcrumbsProps, "children"> {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  maxItems?: number;
  "aria-label"?: string;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  separator = <NavigateNextIcon fontSize="small" />,
  maxItems,
  "aria-label": ariaLabel = "breadcrumb",
  ...props
}) => {
  const theme = useTheme();

  const renderBreadcrumbItem = useCallback(
    (item: BreadcrumbItem, index: number) => {
      const isLast = index === items.length - 1;

      // If it's the last item (current page), render as Typography with background
      if (isLast) {
        return (
          <Typography
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              backgroundColor: theme.palette.breadcrumb.currentPageBackground,
              color: theme.palette.breadcrumb.currentPageTextColor,
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            {item.icon}
            {item.label}
          </Typography>
        );
      }

      // For non-last items, render as Link
      if (item.to) {
        // Use React Router Link for client-side navigation
        return (
          <Link
            key={index}
            component={RouterLink}
            to={item.to}
            underline="hover"
            color="inherit"
            onClick={item.onClick}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            {item.icon}
            {item.label}
          </Link>
        );
      }

      // Fallback to regular link for external URLs
      return (
        <Link
          key={index}
          underline="hover"
          color="inherit"
          href={item.href}
          onClick={item.onClick}
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          {item.icon}
          {item.label}
        </Link>
      );
    },
    [items.length, theme.palette.breadcrumb]
  );

  return (
    <MuiBreadcrumbs separator={separator} maxItems={maxItems} aria-label={ariaLabel} {...props}>
      {items.map(renderBreadcrumbItem)}
    </MuiBreadcrumbs>
  );
};

export default Breadcrumbs;
